import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, Modal, ActivityIndicator, Alert, useWindowDimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../context/AuthContext';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { attendanceAPI, leaveAPI } from '../services/api';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Logger } from '../utils/logger';
import { attendanceTotals, LEAVE_EXCLUDED_NOTE } from '../utils/attendanceTotals';
import { AttendanceOverview, AttendanceSessionInfo } from '../types';
import { WebPageShell } from '../components/common/WebPageShell';
import { DayAttendanceDetail } from '../components/attendance/DayAttendanceDetail';
import { AttendanceHistoryList } from '../components/attendance/AttendanceHistoryList';
import {
  ATTENDANCE_STATUS_COLOR,
  dayKeyOf,
  findDay,
  historyDays,
  toDateKey,
  toSessionInfoList,
} from '../utils/attendanceSessions';
import { Toast } from '../components/common/Toast';
import { useTheme, ThemeColors } from '../context/ThemeContext';

type NavigationProp = NativeStackNavigationProp<any>;
type Tab = 'Daily' | 'Weekly' | 'Monthly';
/**
 * Every status the attendance API can put on the wire.
 *
 * 'Late' and 'Half day' were missing, and because the three maps below are
 * keyed by this type, a day carrying either looked up `undefined` and rendered
 * as an uncoloured cell - indistinguishable from a day nobody marked.
 * 'Half day' arrives only from a school taking two registers a day.
 */
type Status = 'Present' | 'Absent' | 'Leave' | 'Late' | 'Half day' | 'Remote';

const TABS: Tab[] = ['Daily', 'Weekly', 'Monthly'];
const WEEKDAY_SHORT = ['M', 'T', 'W', 'T', 'F', 'S'];
const CALENDAR_WEEKDAY_HEADER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const STATUS_COLOR: Record<Status, string> = ATTENDANCE_STATUS_COLOR;

/** How many earlier days the Daily tab lists under today. */
const RECENT_DAYS_ON_DAILY = 5;

const STATUS_LABEL: Record<Status, string> = {
  Present: 'Present',
  Leave: 'Leave',
  Absent: 'Absent',
  Late: 'Late',
  'Half day': 'Half day',
  Remote: 'Remote',
};

// The weekly bar chart reads as "how much of the day were they there", so a
// half day stands at half height and a late arrival just under a full one.
const STATUS_BAR_HEIGHT: Record<Status, number> = {
  Present: 100,
  Leave: 60,
  Absent: 20,
  Late: 85,
  // Remote is a full day of attendance, so it stands as tall as present.
  'Half day': 50,
  Remote: 100,
};

// Plain CSS for the raw HTML <input type="date"> below — kept independent of the RN
// StyleSheet system since that isn't guaranteed to produce a DOM-compatible style object.
const dateInputStyle: React.CSSProperties = {
  border: '1px solid #E2E8F0',
  borderRadius: 10,
  padding: '10px 14px',
  fontSize: 14,
  color: '#1E293B',
  backgroundColor: '#F8FAFC',
  marginBottom: 16,
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

function mondayIndexOf(dateStr: string): number {
  const d = new Date(`${dateStr.slice(0, 10)}T00:00:00`);
  return (d.getDay() + 6) % 7;
}

interface MonthCell {
  day: number;
  key: string;
  status?: Status;
  isToday: boolean;
}

function buildMonthGrid(year: number, month: number, statusByDate: Map<string, Status>): (MonthCell | null)[] {
  const firstOfMonth = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayKey = toDateKey(new Date());

  const cells: (MonthCell | null)[] = new Array(firstOfMonth.getDay()).fill(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const key = toDateKey(new Date(year, month - 1, day));
    cells.push({ day, key, status: statusByDate.get(key), isToday: key === todayKey });
  }
  return cells;
}

const SubjectProgress = ({ name, percentage, color }: { name: string; percentage: number; color: string }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  return (
    <View style={styles.subjectRow}>
      <View style={styles.subjectHeader}>
        <Text style={styles.subjectName}>{name}</Text>
        <Text style={[styles.subjectPercentage, { color }]}>{percentage}%</Text>
      </View>
      <View style={styles.progressTrack}>
        <View style={[styles.progressFill, { width: `${percentage}%`, backgroundColor: color }]} />
      </View>
    </View>
  );
};

interface Leave {
  id: string;
  fromDate: string;
  toDate: string;
  reason: string;
  appliedDate: string;
}

interface SubjectAttendance {
  subjectId: string;
  name: string;
  percentage: number;
}

// Same semantics the rest of this screen uses: red below the 80% requirement,
// amber when approaching it, blue otherwise.
const subjectColor = (percentage: number) =>
  percentage < 80 ? '#DC2626' : percentage < 90 ? '#F59E0B' : '#2563EB';

export function AttendanceScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width: winWidth } = useWindowDimensions();
  const isMobile = winWidth < 768;

  // Pinned to 'Attendance': Leave Requests moved out to its own page/route
  // (LeavesScreen). Kept rather than deleted so the Leave branch below still
  // compiles and can be revived if the two are ever merged again.
  const [activeSection] = useState<'Attendance' | 'Leave'>('Attendance');

  // Attendance Tab states: 'Daily' | 'Weekly' | 'Monthly'
  const [activeTab, setActiveTab] = useState<Tab>('Weekly');
  const [overview, setOverview] = useState<AttendanceOverview | null>(null);
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(true);
  // The month whose calendar and history are on screen (always its 1st).
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [subjectSummary, setSubjectSummary] = useState<SubjectAttendance[]>([]);
  const [isSubjectLoading, setIsSubjectLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [expectedSessions, setExpectedSessions] = useState<AttendanceSessionInfo[]>([]);

  // The registers the school takes each day, so today's untaken one can be shown
  // as "Not marked yet". Optional: without it the screen still lists what was taken.
  useEffect(() => {
    attendanceAPI
      .getAttendanceSessions()
      .then(res => setExpectedSessions(toSessionInfoList(res.data)))
      .catch(error => Logger.error('Attendance', 'Failed to load attendance sessions', error));
  }, []);

  // "Today" is the device's calendar date - the API's own `today` is picked on the
  // server's UTC clock, which in Cayman is already tomorrow from 7pm.
  const todayKey = toDateKey(new Date());
  const viewMonthKey = toDateKey(currentDate).slice(0, 7);
  const isCurrentMonth = viewMonthKey === todayKey.slice(0, 7);

  // A day's registers open only when the parent taps a date or a history row -
  // today already has the Daily tab. Moving to another month clears the choice.
  useEffect(() => {
    setSelectedKey(null);
  }, [viewMonthKey]);

  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    // Today and this week only exist in the current month's data.
    if (tab !== 'Monthly' && !isCurrentMonth) {
      const d = new Date();
      setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  };

  // Leave state variables
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [isLeavesLoading, setIsLeavesLoading] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveFromDate, setLeaveFromDate] = useState('');
  const [leaveToDate, setLeaveToDate] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [isLeaveSubmitting, setIsLeaveSubmitting] = useState(false);
  const [showLeaveToast, setShowLeaveToast] = useState(false);
  const [leaveToastMessage, setLeaveToastMessage] = useState('');

  const loadAttendance = useCallback(async () => {
    if (!selectedStudent) {
      setIsAttendanceLoading(false);
      return;
    }
    try {
      setIsAttendanceLoading(true);
      const res = await attendanceAPI.getAttendanceOverview(
        selectedStudent.id,
        selectedStudent.classId,
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
      );
      const data = res.data;
      setOverview({
        totalWorkingDays: data?.totalWorkingDays ?? 0,
        totalPresentDays: data?.totalPresentDays ?? 0,
        totalAbsentDays: data?.totalAbsentDays ?? 0,
        totalLeaveDays: data?.totalLeaveDays ?? 0,
        totalPartialDays: data?.totalPartialDays ?? 0,
        totalRemoteDays: data?.totalRemoteDays ?? 0,
        attendancePercentage: data?.attendancePercentage ?? 0,
        today: data?.today ?? null,
        weeklyBreakdown: data?.weeklyBreakdown ?? [],
        monthlyDays: data?.days ?? [],
      });
    } catch (error) {
      Logger.error('Attendance', 'Failed to load attendance overview', error);
    } finally {
      setIsAttendanceLoading(false);
    }
  }, [selectedStudent, currentDate]);

  const loadSubjectSummary = useCallback(async () => {
    if (!selectedStudent) {
      setIsSubjectLoading(false);
      return;
    }
    try {
      setIsSubjectLoading(true);
      const res = await attendanceAPI.getSubjectAttendanceSummary(
        selectedStudent.id,
        selectedStudent.academicYear,
      );
      const mapped: SubjectAttendance[] = (res.data ?? [])
        // A subject with no registers taken yet has no meaningful percentage.
        .filter((s: any) => (s.sessionsHeld ?? s.SessionsHeld ?? 0) > 0)
        .map((s: any) => ({
          subjectId: s.subjectId ?? s.SubjectId ?? '',
          name: s.subjectName ?? s.SubjectName ?? 'Subject',
          percentage: Math.round(s.presencePercent ?? s.PresencePercent ?? 0),
        }));
      setSubjectSummary(mapped);
    } catch (error) {
      Logger.error('Attendance', 'Failed to load subject attendance summary', error);
      setSubjectSummary([]);
    } finally {
      setIsSubjectLoading(false);
    }
  }, [selectedStudent]);

  const loadLeaves = useCallback(async () => {
    if (!selectedStudent) {
      setIsLeavesLoading(false);
      return;
    }
    try {
      setIsLeavesLoading(true);
      const res = await leaveAPI.getLeaveRequests(selectedStudent.id);
      // API returns startDate/endDate/createdDate, not fromDate/toDate/appliedDate —
      // matching those never existed, which is why every date rendered as "Invalid Date".
      const mapped = (res.data ?? []).map((l: any) => ({
        id: l.id || l.Id || '',
        fromDate: l.startDate || l.StartDate || '',
        toDate: l.endDate || l.EndDate || '',
        reason: l.reason || l.Reason || '',
        appliedDate: l.createdDate || l.CreatedDate || '',
      }));
      setLeaves(mapped);
    } catch (error) {
      Logger.error('Leaves', 'Failed to load leaves', error);
    } finally {
      setIsLeavesLoading(false);
    }
  }, [selectedStudent]);

  useEffect(() => {
    if (activeSection === 'Attendance') {
      loadAttendance();
      loadSubjectSummary();
    } else if (activeSection === 'Leave') {
      loadLeaves();
    }
  }, [activeSection, loadAttendance, loadSubjectSummary, loadLeaves]);

  const handleApplyLeave = async () => {
    if (!leaveFromDate || !leaveToDate || !leaveReason.trim() || !selectedStudent) return;
    try {
      setIsLeaveSubmitting(true);
      await leaveAPI.applyLeave({
        studentId: selectedStudent.id,
        fromDate: leaveFromDate,
        toDate: leaveToDate,
        reason: leaveReason.trim(),
      });
      setLeaveFromDate('');
      setLeaveToDate('');
      setLeaveReason('');
      setShowLeaveForm(false);
      setLeaveToastMessage('Leave application submitted successfully.');
      setShowLeaveToast(true);
      await loadLeaves();
    } catch (error) {
      Alert.alert('Error', 'Failed to apply leave.');
      Logger.error('Leaves', 'Failed to apply leave', error);
    } finally {
      setIsLeaveSubmitting(false);
    }
  };

  const calculateDays = (fromDateStr: string, toDateStr: string) => {
    const from = new Date(fromDateStr);
    const to = new Date(toDateStr);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return 0;
    return Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  if (!selectedStudent) {
    return (
      <WebPageShell title="Attendance" maxWidth={1000}>
        <View style={{ padding: 20, alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          <Text style={{ fontSize: 16, color: '#64748B', fontWeight: '500' }}>No student selected</Text>
        </View>
      </WebPageShell>
    );
  }

  // From the days on screen - this week's on Weekly, the month's otherwise - so the counts,
  // the bars and the percentage always agree; approved leave is excluded from the % (see
  // utils/attendanceTotals). The server's month totals counted 5 leave days against
  // 1 "total day" at 100%.
  const totals = attendanceTotals(activeTab === 'Weekly' ? overview?.weeklyBreakdown : overview?.monthlyDays);
  const presentDays = totals.present;
  const leaveDays = totals.leave;
  const absentDays = totals.absent;
  const halfDays = totals.halfDay;
  const remoteDays = totals.remote;
  const totalDays = totals.totalDays;
  const attendancePercentage = totals.percentage ?? 0;

  const weekSlots: (Status | null)[] = Array(6).fill(null);
  (overview?.weeklyBreakdown ?? []).forEach(day => {
    const idx = mondayIndexOf(day.date);
    if (idx >= 0 && idx < 6) {
      weekSlots[idx] = day.status;
    }
  });

  // Local calendar date - toISOString() is UTC and was already tomorrow each evening.
  const todayIsoDate = todayKey;
  // Built from the month on screen. These used to read `now`, so the arrows
  // fetched another month's data but kept drawing the current month's grid.
  const monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const statusByDate = new Map((overview?.monthlyDays ?? []).map(d => [dayKeyOf(d.date), d.status]));
  const monthGrid = buildMonthGrid(currentDate.getFullYear(), currentDate.getMonth() + 1, statusByDate);
  const monthHistory = historyDays(overview?.monthlyDays, todayKey);
  const selectedDay = selectedKey ? findDay(overview?.monthlyDays, selectedKey) : null;

  // Derived from the month actually on screen. Present/Leave/Absent are always
  // shown so the key reads the same from month to month; Late and Half day join
  // it only where the month contains one, which for a school taking a single
  // daily register is never.
  const legendStatuses: Status[] = (['Present', 'Leave', 'Absent'] as Status[]).concat(
    (['Late', 'Half day', 'Remote'] as Status[]).filter(s =>
      [...statusByDate.values()].includes(s),
    ),
  );

  // Looked up by the device's date in the month's days; the API's `today` is used
  // only if it names the same calendar day.
  const apiToday = overview?.today && dayKeyOf(overview.today.date) === todayKey ? overview.today : null;
  const todayDay = isCurrentMonth ? findDay(overview?.monthlyDays, todayKey) ?? apiToday : null;
  const recentDays = monthHistory.filter(d => dayKeyOf(d.date) !== todayKey).slice(0, RECENT_DAYS_ON_DAILY);

  return (
    <WebPageShell title="Attendance" maxWidth={1000}>

      {/* Leave Requests is its own page (LeavesScreen, sidebar "Leave requests"),
          so this screen no longer carries an Attendance/Leave section switch.
          activeSection is pinned to 'Attendance' below; the Leave branch and its
          state are kept in place for reference rather than deleted. */}

      {activeSection === 'Attendance' ? (
        // A spinner only for the first load; switching month keeps the page and shows a
        // small spinner beside the month name instead. Not fullScreen: it sits inside the
        // page's card, where the navy full-screen loader would be a dark block.
        isAttendanceLoading && !overview ? (
          <LoadingSpinner message="Loading attendance..." />
        ) : (
          <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>

            {/* Segmented Control */}
            <View style={styles.segmentedControl}>
              {TABS.map((tab) => (
                <TouchableOpacity
                  key={tab}
                  style={[styles.segmentBtn, activeTab === tab && styles.segmentBtnActive]}
                  onPress={() => selectTab(tab)}
                >
                  <Text style={[styles.segmentText, activeTab === tab && styles.segmentTextActive]}>
                    {tab}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {activeTab === 'Daily' ? (
              <>
                {/* Today, register by register */}
                <View style={styles.card}>
                  <DayAttendanceDetail
                    title="Today"
                    dateKey={todayKey}
                    day={todayDay}
                    expectedSessions={expectedSessions}
                    isToday
                  />
                </View>

                {/* The last few school days, so a parent sees the pattern at a glance */}
                <View style={styles.card}>
                  <View style={styles.cardHeaderRow}>
                    <Text style={[styles.cardTitle, { marginBottom: 0 }]}>Recent days</Text>
                    <TouchableOpacity onPress={() => selectTab('Monthly')}>
                      <Text style={styles.linkText}>Full history</Text>
                    </TouchableOpacity>
                  </View>
                  <AttendanceHistoryList
                    days={recentDays}
                    emptyText="No earlier days recorded this month."
                    onSelect={key => {
                      setSelectedKey(key);
                      selectTab('Monthly');
                    }}
                  />
                </View>
              </>
            ) : (
              <>
                {/* Summary Card */}
                <View style={styles.card}>
                  <View style={[styles.summaryRow, isMobile && styles.summaryRowMobile]}>
                    {/* Donut Chart Mock */}
                    <View style={[styles.donutContainer, isMobile && { marginRight: 0, marginBottom: 20 }]}>
                      <View style={styles.donutRing}>
                        <View style={styles.donutMockRed} />
                        <View style={styles.donutMockYellow} />
                        <View style={styles.donutInner}>
                          <Text style={styles.donutValue}>{totals.percentage === null ? 'N/A' : `${attendancePercentage}%`}</Text>
                          <Text style={styles.donutLabel}>PRESENT</Text>
                        </View>
                      </View>
                    </View>

                    {/* Legend */}
                    <View style={styles.legendContainer}>
                      <View style={styles.legendRow}>
                        <View style={styles.legendLabelGroup}>
                          <View style={[styles.legendDot, { backgroundColor: STATUS_COLOR.Present }]} />
                          <Text style={styles.legendLabelText}>Present</Text>
                        </View>
                        <Text style={styles.legendValueText}>{presentDays}</Text>
                      </View>
                      <View style={styles.legendRow}>
                        <View style={styles.legendLabelGroup}>
                          <View style={[styles.legendDot, { backgroundColor: STATUS_COLOR.Leave }]} />
                          <Text style={styles.legendLabelText}>Leave</Text>
                        </View>
                        <Text style={styles.legendValueText}>{leaveDays}</Text>
                      </View>
                      <View style={styles.legendRow}>
                        <View style={styles.legendLabelGroup}>
                          <View style={[styles.legendDot, { backgroundColor: STATUS_COLOR.Absent }]} />
                          <Text style={styles.legendLabelText}>Absent</Text>
                        </View>
                        <Text style={styles.legendValueText}>{absentDays}</Text>
                      </View>

                      {halfDays > 0 && (
                        <View style={styles.legendRow}>
                          <View style={styles.legendLabelGroup}>
                            <View style={[styles.legendDot, { backgroundColor: STATUS_COLOR['Half day'] }]} />
                            <Text style={styles.legendLabelText}>Half day</Text>
                          </View>
                          <Text style={styles.legendValueText}>{halfDays}</Text>
                        </View>
                      )}
                      {remoteDays > 0 && (
                        <View style={styles.legendRow}>
                          <View style={styles.legendLabelGroup}>
                            <View style={[styles.legendDot, { backgroundColor: STATUS_COLOR.Remote }]} />
                            <Text style={styles.legendLabelText}>Remote</Text>
                          </View>
                          <Text style={styles.legendValueText}>{remoteDays}</Text>
                        </View>
                      )}

                      <View style={styles.legendDivider} />

                      <View style={styles.legendRow}>
                        <Text style={styles.legendTotalText}>Total days</Text>
                        <Text style={styles.legendTotalValue}>{totalDays}</Text>
                      </View>
                      <Text style={styles.leaveNote}>{LEAVE_EXCLUDED_NOTE}</Text>
                    </View>
                  </View>
                </View>

                {activeTab === 'Weekly' ? (
                  /* This week Bar Chart */
                  <View style={styles.card}>
                    <Text style={styles.cardTitle}>This week</Text>
                    <View style={styles.chartContainer}>
                      {weekSlots.map((status, index) => (
                        <View key={index} style={styles.chartColumn}>
                          <View
                            style={[
                              styles.bar,
                              {
                                height: status ? STATUS_BAR_HEIGHT[status] : 8,
                                backgroundColor: status ? STATUS_COLOR[status] : '#E2E8F0',
                              },
                            ]}
                          />
                          <Text style={styles.chartLabel}>{WEEKDAY_SHORT[index]}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                ) : (
                  <>
                    {/* Monthly calendar grid - click a day to see its registers */}
                    <View style={styles.card}>
                      <View style={styles.monthHeaderRow}>
                        <TouchableOpacity
                          style={styles.monthNavBtn}
                          onPress={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                          accessibilityLabel="Previous month"
                        >
                          <FeatherIcon name="chevron-left" size={18} color={colors.textSecondary} />
                        </TouchableOpacity>
                        <View style={styles.monthTitleWrap}>
                          <Text style={[styles.cardTitle, { marginBottom: 0 }]}>{monthLabel}</Text>
                          {isAttendanceLoading && (
                            <ActivityIndicator size="small" color={colors.primary} style={{ marginLeft: 8 }} />
                          )}
                        </View>
                        <TouchableOpacity
                          style={styles.monthNavBtn}
                          onPress={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                          accessibilityLabel="Next month"
                        >
                          <FeatherIcon name="chevron-right" size={18} color={colors.textSecondary} />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.calendarHeaderRow}>
                        {CALENDAR_WEEKDAY_HEADER.map((label, index) => (
                          <Text key={index} style={styles.calendarHeaderText}>{label}</Text>
                        ))}
                      </View>
                      <View style={styles.calendarGrid}>
                        {monthGrid.map((cell, index) => (
                          <View key={index} style={styles.calendarCellWrapper}>
                            {cell && (
                              <TouchableOpacity
                                activeOpacity={0.7}
                                onPress={() => setSelectedKey(cell.key)}
                                accessibilityLabel={`${cell.day} ${monthLabel}${cell.status ? `, ${cell.status}` : ''}`}
                                style={[
                                  styles.calendarCell,
                                  cell.status && { backgroundColor: STATUS_COLOR[cell.status] },
                                  cell.isToday && styles.calendarCellToday,
                                  cell.key === selectedKey && styles.calendarCellSelected,
                                ]}
                              >
                                <Text style={[styles.calendarCellText, cell.status && styles.calendarCellTextActive]}>
                                  {cell.day}
                                </Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        ))}
                      </View>
                      <View style={styles.calendarLegendRow}>
                        {legendStatuses.map(status => (
                          <View key={status} style={styles.calendarLegendItem}>
                            <View style={[styles.legendDot, { backgroundColor: STATUS_COLOR[status] }]} />
                            <Text style={styles.legendLabelText}>{STATUS_LABEL[status]}</Text>
                          </View>
                        ))}
                      </View>
                    </View>

                    {/* The selected day, register by register */}
                    {selectedKey && (
                      <View style={styles.card}>
                        <DayAttendanceDetail
                          title={selectedKey === todayKey ? 'Today' : undefined}
                          dateKey={selectedKey}
                          day={selectedKey === todayKey ? todayDay : selectedDay}
                          expectedSessions={expectedSessions}
                          isToday={selectedKey === todayKey}
                        />
                      </View>
                    )}
                  </>
                )}

                {/* Warning Banner */}
                    {/* No register for the child yet (just enrolled) is not low attendance (bug 4556). */}
                {totals.percentage !== null && attendancePercentage < 80 && (
                  <View style={styles.warningBanner}>
                    <FeatherIcon name="alert-triangle" size={20} color="#D97706" style={{ marginTop: 2 }} />
                    <View style={styles.warningTextContainer}>
                      <Text style={styles.warningTitle}>Low attendance warning</Text>
                      <Text style={styles.warningSubtitle}>
                        Attendance is {attendancePercentage}% — below the 80% requirement.
                      </Text>
                    </View>
                  </View>
                )}

                {/* By subject Card — real per-subject percentages from
                    /school/subject-attendance/student/{id}/summary */}
                <View style={styles.card}>
                  <Text style={styles.cardTitle}>By subject</Text>

                  {isSubjectLoading ? (
                    <LoadingSpinner size="small" message="Loading subjects..." />
                  ) : subjectSummary.length === 0 ? (
                    <Text style={{ fontSize: 14, color: '#64748B', paddingVertical: 16, textAlign: 'center' }}>
                      No subject-wise attendance has been recorded yet.
                    </Text>
                  ) : (
                    subjectSummary.map(subject => (
                      <SubjectProgress
                        key={subject.subjectId || subject.name}
                        name={subject.name}
                        percentage={subject.percentage}
                        color={subjectColor(subject.percentage)}
                      />
                    ))
                  )}
                </View>
              </>
            )}
          </ScrollView>
        )
      ) : (
        /* Leave requests section */
        isLeavesLoading ? (
          <LoadingSpinner message="Loading leave applications..." />
        ) : (
          <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>

            {/* Title & Apply Button Header */}
            <View style={[styles.leavesHeader, isMobile && styles.leavesHeaderMobile]}>
              <View>
                <Text style={styles.leavesTitle}>Leave History</Text>
                <Text style={styles.leavesSubtitle}>Manage and review your child's leave applications</Text>
              </View>
              <TouchableOpacity style={styles.applyLeaveBtn} onPress={() => setShowLeaveForm(true)}>
                <FeatherIcon name="plus" size={16} color="#FFF" />
                <Text style={styles.applyLeaveBtnText}>Apply Leave</Text>
              </TouchableOpacity>
            </View>

            {leaves.length === 0 ? (
              <View style={styles.emptyStateContainer}>
                <FeatherIcon name="calendar" size={48} color="#CBD5E1" />
                <Text style={styles.emptyStateTitle}>No Leave Applications</Text>
                <Text style={styles.emptyStateSubtitle}>
                  You haven't submitted any leave applications for {selectedStudent.name} yet.
                </Text>
              </View>
            ) : (
              leaves.map(item => {
                const days = calculateDays(item.fromDate, item.toDate);
                return (
                  <View key={item.id} style={styles.leaveCard}>
                    <View style={styles.leaveCardHeader}>
                      <View>
                        <Text style={styles.leaveDateRange}>
                          {new Date(item.fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(item.toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Text>
                        <Text style={styles.leaveDaysCount}>{days} day{days > 1 ? 's' : ''}</Text>
                      </View>
                    </View>
                    <Text style={styles.leaveReasonText}>{item.reason}</Text>
                    <View style={styles.leaveDivider} />
                    <View style={styles.leaveCardFooter}>
                      <FeatherIcon name="clock" size={12} color="#94A3B8" />
                      <Text style={styles.leaveAppliedOn}>
                        Applied on {new Date(item.appliedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </Text>
                    </View>
                  </View>
                );
              })
            )}

            {/* Apply Leave Modal */}
            <Modal visible={showLeaveForm} transparent animationType="fade">
              <View style={styles.modalOverlay}>
                <View style={styles.modalContent}>
                  <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>New Leave Application</Text>
                    <TouchableOpacity onPress={() => setShowLeaveForm(false)} style={styles.modalCloseBtn}>
                      <FeatherIcon name="x" size={20} color="#64748B" />
                    </TouchableOpacity>
                  </View>

                  <View style={styles.modalBody}>
                    <Text style={styles.inputLabel}>From Date <Text style={styles.required}>*</Text></Text>
                    {/* A real HTML date input rather than a free-text field — this file only ever
                        renders on web (react-native-web), so a lowercase DOM tag here is a real
                        <input>, not a RN component, and gives the browser's native calendar picker. */}
                    <input
                      type="date"
                      style={dateInputStyle}
                      value={leaveFromDate}
                      min={todayIsoDate}
                      onChange={(e: any) => setLeaveFromDate(e.target.value)}
                      disabled={isLeaveSubmitting}
                    />

                    <Text style={styles.inputLabel}>To Date <Text style={styles.required}>*</Text></Text>
                    <input
                      type="date"
                      style={dateInputStyle}
                      value={leaveToDate}
                      min={leaveFromDate || todayIsoDate}
                      onChange={(e: any) => setLeaveToDate(e.target.value)}
                      disabled={isLeaveSubmitting}
                    />

                    <Text style={styles.inputLabel}>Reason for Leave <Text style={styles.required}>*</Text></Text>
                    <TextInput
                      style={[styles.modalInput, { minHeight: 90, textAlignVertical: 'top' }]}
                      placeholder="Please describe the reason for leave..."
                      multiline
                      numberOfLines={4}
                      value={leaveReason}
                      onChangeText={setLeaveReason}
                      editable={!isLeaveSubmitting}
                    />
                  </View>

                  <View style={styles.modalFooter}>
                    <TouchableOpacity
                      style={styles.modalCancelBtn}
                      onPress={() => setShowLeaveForm(false)}
                      disabled={isLeaveSubmitting}
                    >
                      <Text style={styles.modalCancelBtnText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.modalSubmitBtn, (isLeaveSubmitting || !leaveFromDate || !leaveToDate || !leaveReason.trim()) && styles.modalSubmitBtnDisabled]}
                      onPress={handleApplyLeave}
                      disabled={isLeaveSubmitting || !leaveFromDate || !leaveToDate || !leaveReason.trim()}
                    >
                      {isLeaveSubmitting ? (
                        <ActivityIndicator size="small" color="#FFF" />
                      ) : (
                        <Text style={styles.modalSubmitBtnText}>Submit</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </Modal>

          </ScrollView>
        )
      )}

      <Toast
        visible={showLeaveToast}
        message={leaveToastMessage}
        type="success"
        onDismiss={() => setShowLeaveToast(false)}
      />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  mainTabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    marginBottom: 20,
  },
  mainTabBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  mainTabBtnActive: {
    borderBottomColor: '#2563EB',
  },
  mainTabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  mainTabTextActive: {
    color: '#2563EB',
    fontWeight: '700',
  },
  scroll: { flex: 1 },
  content: { paddingHorizontal: 20, paddingTop: 10, paddingBottom: 40 },
  screenHeader: { marginBottom: 20 },
  headerTitleContainer: { alignItems: 'flex-start' },
  screenTitle: { fontSize: 22, fontWeight: '800', color: colors.textPrimary },
  screenSubtitle: { fontSize: 12, color: colors.textSecondary, fontWeight: '500', marginTop: 4 },
  segmentedControl: { flexDirection: 'row', backgroundColor: colors.border, borderRadius: 12, padding: 4, marginBottom: 20 },
  segmentBtn: { flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  segmentBtnActive: { backgroundColor: colors.card, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 2 },
  segmentText: { fontSize: 13, fontWeight: '600', color: colors.textSecondary },
  segmentTextActive: { color: colors.textPrimary, fontWeight: '700' },
  card: { backgroundColor: colors.card, borderRadius: 20, padding: 20, marginBottom: 16, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 10, shadowOffset: { width: 0, height: 4 }, elevation: 3 },
  cardTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary, marginBottom: 16 },
  dailyDateText: { fontSize: 14, color: colors.textSecondary, marginBottom: 12, fontWeight: '500' },
  dailyStatusBadge: { borderWidth: 1, borderRadius: 12, paddingVertical: 12, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', gap: 8 },
  dailyStatusText: { fontSize: 14, fontWeight: '700' },
  dailyNoRecordText: { fontSize: 14, color: colors.textMuted, fontStyle: 'italic' },
  summaryRow: { flexDirection: 'row', alignItems: 'center' },
  summaryRowMobile: { flexDirection: 'column', alignItems: 'stretch' },
  donutContainer: { width: 130, height: 130, marginRight: 20, alignSelf: 'center' },
  donutRing: { width: '100%', height: '100%', borderRadius: 65, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  donutMockRed: { width: '100%', height: '30%', backgroundColor: '#DC2626', position: 'absolute', top: 0 },
  donutMockYellow: { width: '100%', height: '20%', backgroundColor: '#F59E0B', position: 'absolute', top: '30%' },
  donutInner: { width: 100, height: 100, borderRadius: 50, backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center' },
  donutValue: { fontSize: 22, fontWeight: '800', color: colors.textPrimary },
  donutLabel: { fontSize: 10, fontWeight: '700', color: colors.textSecondary, marginTop: 2 },
  legendContainer: { flex: 1 },
  legendRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  legendLabelGroup: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendLabelText: { fontSize: 12, color: colors.textSecondary, fontWeight: '500' },
  legendValueText: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
  legendDivider: { height: 1, backgroundColor: colors.border, marginVertical: 12 },
  legendTotalText: { fontSize: 13, fontWeight: '600', color: colors.textSecondary },
  leaveNote: { marginTop: 8, fontSize: 11, lineHeight: 15, color: colors.textSecondary },
  legendTotalValue: { fontSize: 16, fontWeight: '800', color: colors.textPrimary },
  chartContainer: { flexDirection: 'row', justifyContent: 'space-around', alignItems: 'flex-end', height: 120 },
  chartColumn: { alignItems: 'center', gap: 8 },
  bar: { width: 24, borderRadius: 6 },
  chartLabel: { fontSize: 12, fontWeight: '600', color: colors.textSecondary },
  calendarHeaderRow: { flexDirection: 'row', marginBottom: 12 },
  calendarHeaderText: { flex: 1, textAlign: 'center', fontSize: 12, fontWeight: '700', color: colors.textMuted, paddingVertical: 8 },
  calendarGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 16 },
  calendarCellWrapper: { width: '14.28%', aspectRatio: 1, justifyContent: 'center', alignItems: 'center', paddingBottom: 4 },
  calendarCell: { width: '90%', height: '90%', borderRadius: 8, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.divider },
  calendarCellToday: { borderWidth: 2, borderColor: colors.primary },
  calendarCellSelected: { borderWidth: 2, borderColor: colors.textPrimary, transform: [{ scale: 1.1 }] },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  linkText: { fontSize: 13, fontWeight: '700', color: colors.primary },
  monthHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  monthTitleWrap: { flexDirection: 'row', alignItems: 'center' },
  monthNavBtn: {
    width: 34,
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarCellText: { fontSize: 12, fontWeight: '600', color: colors.textSecondary },
  calendarCellTextActive: { color: '#FFFFFF', fontWeight: '700' },
  calendarLegendRow: { flexDirection: 'row', gap: 24, paddingTop: 12, borderTopWidth: 1, borderTopColor: '#E2E8F0' },
  calendarLegendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  warningBanner: { backgroundColor: '#FFFBEB', borderLeftWidth: 4, borderLeftColor: '#D97706', borderRadius: 12, padding: 16, marginBottom: 16, flexDirection: 'row', gap: 12 },
  warningTextContainer: { flex: 1 },
  warningTitle: { fontSize: 14, fontWeight: '700', color: '#92400E' },
  warningSubtitle: { fontSize: 12, color: '#B45309', marginTop: 2 },
  subjectRow: { marginBottom: 16 },
  subjectHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  subjectName: { fontSize: 13, fontWeight: '600', color: colors.textSecondary },
  subjectPercentage: { fontSize: 13, fontWeight: '700' },
  progressTrack: { height: 6, backgroundColor: colors.divider, borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3 },
  bottomActions: { flexDirection: 'row', gap: 12 },
  pdfBtn: { flex: 1, backgroundColor: '#2563EB', borderRadius: 12, paddingVertical: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  pdfBtnText: { color: '#FFFFFF', fontSize: 14, fontWeight: '700' },
  excelBtn: { flex: 1, backgroundColor: '#F1F5F9', borderRadius: 12, paddingVertical: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderWidth: 1, borderColor: '#E2E8F0' },
  excelBtnText: { color: '#1E293B', fontSize: 14, fontWeight: '700' },
  leavesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  leavesHeaderMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  leavesTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#1E293B',
  },
  leavesSubtitle: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '500',
    marginTop: 2,
  },
  applyLeaveBtn: {
    backgroundColor: '#2563EB',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  applyLeaveBtnText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },
  emptyStateContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 20,
    paddingVertical: 40,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderStyle: 'dashed',
    gap: 12,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#475569',
  },
  emptyStateSubtitle: {
    fontSize: 13,
    color: '#64748B',
    textAlign: 'center',
    maxWidth: 320,
  },
  leaveCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 12,
    shadowColor: '#1E293B',
    shadowOpacity: 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  leaveCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  leaveDateRange: {
    fontSize: 15,
    fontWeight: '800',
    color: '#1E293B',
  },
  leaveDaysCount: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
    marginTop: 2,
  },
  leaveReasonText: {
    fontSize: 14,
    color: '#475569',
    lineHeight: 20,
    marginBottom: 16,
  },
  leaveDivider: {
    height: 1,
    backgroundColor: '#E2E8F0',
    marginBottom: 12,
  },
  leaveCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  leaveAppliedOn: {
    fontSize: 12,
    color: '#94A3B8',
    fontWeight: '500',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    padding: 24,
    width: '90%',
    maxWidth: 500,
    shadowColor: '#0F172A',
    shadowOpacity: 0.1,
    shadowRadius: 15,
    shadowOffset: { width: 0, height: 8 },
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#1E293B',
  },
  modalCloseBtn: {
    padding: 4,
    borderRadius: 8,
  },
  modalBody: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#64748B',
    marginBottom: 6,
  },
  required: {
    color: '#DC2626',
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: '#1E293B',
    backgroundColor: '#F8FAFC',
    marginBottom: 16,
  },
  modalFooter: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  modalCancelBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#64748B',
  },
  modalSubmitBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: '#2563EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalSubmitBtnDisabled: {
    opacity: 0.5,
  },
  modalSubmitBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
